import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForward from '@mui/icons-material/ArrowForward';
import FormatQuote from '@mui/icons-material/FormatQuote';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { buildMockExamsPath } from '../../utils/contentRoutes';
import { ANCHOR_SCROLL_MARGIN, CONTENT_MAX_WIDTH, ctaPrimarySx } from '../../styles/tokens';
import { BORDER, INK, MUTED, SHADOW_CARD, SURFACE } from '../../pages/Practice/map/mapTokens';
import { MOCK_EXAM_TEASER, SECTION_IDS } from './homeContent';
import { IconTile, SectionHeading, bandSx } from './homeUi';

/**
 * Replaces the buried "Take Mock Oral Exams" button, which used to be the second
 * of three identical gradient pills roughly four screens down the page. The
 * feature is the product's strongest and was the least visible thing on it.
 */
const MockExamTeaser = ({ tone }) => {
  const { language } = useLanguage();
  const { isPremium } = useAuth();

  return (
    <Box
      component="section"
      id={SECTION_IDS.rehearse}
      sx={{ ...bandSx(tone), scrollMarginTop: ANCHOR_SCROLL_MARGIN, '&:focus': { outline: 'none' } }}
    >
      <SectionHeading title={MOCK_EXAM_TEASER.title} subtitle={MOCK_EXAM_TEASER.subtitle} chapter="rehearse" />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 3, md: 5 },
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          alignItems: 'stretch',
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        {/* `space-between` rather than a fixed gap: the steps spread to the full
            column height so the last one finishes level with the button opposite,
            instead of stopping ~30px short and leaving the section ragged. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            justifyContent: { md: 'space-between' },
          }}
        >
          {MOCK_EXAM_TEASER.steps.map((step) => (
            <Box key={step.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <IconTile name={step.icon} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  component="h3"
                  sx={{ color: 'primary.main', fontWeight: 800, lineHeight: 1.35 }}
                >
                  {step.title}
                </Typography>
                <Typography sx={{ color: INK, fontSize: '0.9375rem', lineHeight: 1.65 }}>{step.text}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* A concrete task does more to explain a mock exam than any description. */}
          <Box
            sx={{
              position: 'relative',
              p: { xs: 2.5, md: 3 },
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: '16px',
              boxShadow: SHADOW_CARD,
            }}
          >
            <FormatQuote aria-hidden sx={{ position: 'absolute', top: 12, right: 12, color: BORDER, fontSize: 40 }} />
            <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>
              {MOCK_EXAM_TEASER.sample.label}
            </Typography>
            <Typography sx={{ color: INK, fontSize: '1.0625rem', lineHeight: 1.6, fontWeight: 600 }}>
              {MOCK_EXAM_TEASER.sample.prompt}
            </Typography>
          </Box>

          <Typography sx={{ color: MUTED, fontSize: '0.9375rem', lineHeight: 1.7 }}>
            {MOCK_EXAM_TEASER.differentiator}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              order: { xs: -1, md: 0 },
            }}
          >
            <Button
              component={RouterLink}
              to={buildMockExamsPath(language, 'oral', { explicit: true })}
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={ctaPrimarySx}
            >
              {MOCK_EXAM_TEASER.cta}
            </Button>
            <Button
              component={RouterLink}
              to={buildMockExamsPath(language, 'listening')}
              variant="outlined"
              size="large"
              startIcon={!isPremium ? <LockIcon fontSize="small" /> : undefined}
            >
              {MOCK_EXAM_TEASER.ctaListening}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MockExamTeaser;
